import React, { useState, useEffect, useCallback } from 'react';
import { analyticsService } from '../../services/analyticsService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

const ConsumptionPatternsChart = () => {
  const [patterns, setPatterns] = useState(null);
  const [trends, setTrends] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [activeTab, setActiveTab] = useState('patterns');
  const { user } = useAuth();

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [patternsData, trendsData, customersData] = await Promise.all([
        analyticsService.getConsumptionPatterns(user.companyId, selectedPeriod),
        analyticsService.getTemporalTrends(user.companyId, selectedPeriod),
        analyticsService.getTopCustomers(user.companyId, selectedPeriod, 10)
      ]);

      setPatterns(patternsData);
      setTrends(trendsData);
      setTopCustomers(customersData);
    } catch (err) {
      setError('Erro ao carregar dados de análise: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.companyId, selectedPeriod]);

  useEffect(() => {
    if (user?.companyId) {
      loadAnalytics();
    }
  }, [user, selectedPeriod, loadAnalytics]);

  const getMaxValue = (data) => {
    return Math.max(...Object.values(data).map(item => item.count));
  };

  const getBarHeight = (value, maxValue) => {
    return maxValue > 0 ? (value / maxValue) * 100 : 0;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button 
          onClick={loadAnalytics}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 sm:mb-0">
          Análise de Consumo
        </h2>
        
        {/* Seletor de período */}
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">
            Período:
          </label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={7}>7 dias</option>
            <option value={15}>15 dias</option>
            <option value={30}>30 dias</option>
            <option value={60}>60 dias</option>
            <option value={90}>90 dias</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('patterns')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'patterns'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Padrões de Uso
        </button>
        <button
          onClick={() => setActiveTab('trends')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'trends'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Tendências
        </button>
        <button
          onClick={() => setActiveTab('customers')}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === 'customers'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Top Clientes
        </button>
      </div>

      {/* Resumo */}
      {patterns && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800">Total de Transações</h3>
            <p className="text-2xl font-bold text-blue-600">
              {patterns.summary.totalTransactions}
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-green-800">Total de Pontos</h3>
            <p className="text-2xl font-bold text-green-600">
              {patterns.summary.totalPoints.toLocaleString('pt-BR')}
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-purple-800">Dia Mais Ativo</h3>
            <p className="text-lg font-bold text-purple-600">
              {patterns.summary.mostActiveDay}
            </p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-orange-800">Horário de Pico</h3>
            <p className="text-lg font-bold text-orange-600">
              {patterns.summary.mostActiveHour}
            </p>
          </div>
        </div>
      )}

      {/* Conteúdo das abas */}
      {activeTab === 'patterns' && patterns && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gráfico por dia da semana */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Transações por Dia da Semana
            </h3>
            <div className="space-y-3">
              {Object.entries(patterns.byDayOfWeek).map(([day, data]) => {
                const maxValue = getMaxValue(patterns.byDayOfWeek);
                const barWidth = getBarHeight(data.count, maxValue);
                
                return (
                  <div key={day} className="flex items-center">
                    <div className="w-20 text-sm font-medium text-gray-700">
                      {day}
                    </div>
                    <div className="flex-1 mx-3 bg-gray-200 rounded-full h-4 relative">
                      <div
                        className="bg-blue-500 h-4 rounded-full transition-all duration-300"
                        style={{ width: `${barWidth}%` }}
                      />
                      <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
                        {data.count}
                      </span>
                    </div>
                    <div className="w-16 text-xs text-gray-500 text-right">
                      {data.totalPoints.toLocaleString('pt-BR')} pts
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Gráfico por horário */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Transações por Horário
            </h3>
            <div className="grid grid-cols-6 gap-1">
              {Object.entries(patterns.byHour).map(([hour, data]) => {
                const maxValue = getMaxValue(patterns.byHour);
                const barHeight = getBarHeight(data.count, maxValue);
                
                return (
                  <div key={hour} className="text-center">
                    <div className="h-16 flex items-end justify-center">
                      <div
                        className="bg-green-500 w-8 transition-all duration-300 min-h-1 relative group"
                        style={{ height: `${Math.max(barHeight, 5)}%` }}
                        title={`${data.label}: ${data.count} transações`}
                      >
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          {data.count} trans.
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {hour.toString().padStart(2, '0')}h
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'trends' && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Tendência Diária ({selectedPeriod} dias)
          </h3>
          {trends.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {trends.map((day, index) => (
                <div key={day.date} className="flex items-center justify-between py-2 px-3 bg-white rounded border">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-700 w-16">
                      {formatDate(day.date)}
                    </span>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {day.transactions} trans.
                      </span>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        +{day.addedPoints}
                      </span>
                      {day.redeemedPoints > 0 && (
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                          -{day.redeemedPoints}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-gray-800">
                    {day.totalPoints.toLocaleString('pt-BR')} pts
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Nenhum dado encontrado para o período selecionado
            </p>
          )}
        </div>
      )}

      {activeTab === 'customers' && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Clientes Mais Ativos ({selectedPeriod} dias)
          </h3>
          {topCustomers.length > 0 ? (
            <div className="space-y-2">
              {topCustomers.map((customer, index) => (
                <div key={customer.customerId} className="flex items-center justify-between py-3 px-4 bg-white rounded border">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-500 text-white rounded-full text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{customer.name}</p>
                      <p className="text-sm text-gray-500">{customer.phone}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-gray-800">
                      {customer.transactions} transações
                    </p>
                    <div className="flex space-x-2 text-xs">
                      <span className="text-green-600">+{customer.addedPoints}</span>
                      {customer.redeemedPoints > 0 && (
                        <span className="text-red-600">-{customer.redeemedPoints}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              Nenhum cliente encontrado para o período selecionado
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ConsumptionPatternsChart;
